import {
    Card,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardMainAction,
    CardOpenIcon,
    CardStart,
    CardTitle,
} from '@plato/ui/containers/card';
import { intlFormat, isSameDay, parseISO } from 'date-fns';
import type { LucideProps } from 'lucide-react';
import {
    AlignLeftIcon,
    CalendarIcon,
    CircleUserIcon,
    ClockIcon,
    MapPinIcon,
    NotebookPenIcon,
    PaperclipIcon,
    PencilIcon,
    RefreshCwIcon,
    StoreIcon,
    Trash2Icon,
    TypeIcon,
    XIcon,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingBlockFullPage } from '../../../components/loading/loading-block-full-page.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { LinkText } from '../../../components/ui/link-text.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { buildGoogleMapsSearchUrl } from '../../../lib/google-maps-url.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';
import { getEventMetadata } from '../../../utils/event-utils.ts';
import { getRecurrenceWeeksCount } from '../../../utils/rrule.ts';
import { formatISOTimeForDisplay } from '../../../utils/time-utils.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import type { ModalInnerProps } from '../../modal/index.tsx';
import { EditNoteDialog } from '../../notes/components/edit-note-dialog.tsx';
import { LogInteractionDialog } from '../../notes/components/log-interaction-dialog.tsx';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';
import { DeleteEventModal } from './delete-event-modal.tsx';
import { EventEditDialog } from './event-edit-dialog.tsx';
import { EventLogButton } from './event-log-button.tsx';

type EventDetailsDialogProps = ModalInnerProps & {
    eventId: string;
    date: string;
};

export type InfoTagProps = {
    icon: ComponentType<LucideProps>;
} & React.HTMLAttributes<HTMLDivElement>;

export const InfoTag = ({ icon: Icon, children, ...props }: InfoTagProps): ReactNode => {
    if (!children) {
        return null;
    }

    return (
        <div className="flex items-center overflow-hidden gap-2" {...props}>
            <Icon className="h-4 w-4 shrink-0 text-neutral-500" />
            {children}
        </div>
    );
};

const EventDetailsDialogInternal = ({ eventId, date }: EventDetailsDialogProps): ReactNode => {
    const { visible, remove } = useDialog();
    const { t, locale } = useLocale('events');
    const sendEvent = useAnalytics();
    const { userId } = useAppConfig();
    const navigate = useNavigate();
    const utils = api.useUtils();

    const { data: eventDetails, isLoading } = api.platoUser.query.events.getEventById.useQuery(
        { eventId },
        { enabled: !!eventId },
    );

    const isOwner = eventDetails?.owner_id === userId;

    const handleEdit = (): void => {
        if (!eventDetails) {
            return;
        }

        remove();

        void DialogStateManager.show(EventEditDialog, {
            eventId,
            eventDetails,
            occurrenceDate: date,
        });
    };

    const refreshAndClose = (): void => {
        if (!eventDetails) {
            return;
        }

        void Promise.all([
            utils.platoUser.query.events.getEventById.invalidate({
                eventId: eventDetails.event_id,
            }),
            utils.platoUser.query.events.getList.invalidate(),
            (async () => {
                if (eventDetails.customer?.customerId) {
                    await utils.platoUser.query.events.getUpcomingCustomerEvents.invalidate({
                        customerId: eventDetails.customer.customerId,
                    });
                }
            })(),
        ]);

        remove();
    };

    const handleOpenNote = (noteId: string): void => {
        void DialogStateManager.show(EditNoteDialog, {
            onDelete: refreshAndClose,
            onSuccess: refreshAndClose,
            noteId,
        });
    };

    const handleLog = (): void => {
        if (!eventDetails) {
            return;
        }

        const occurrenceDateObj = new Date(date);
        const existingNote = eventDetails.notes.find((n) => isSameDay(n.occurredAt, occurrenceDateObj));

        if (existingNote) {
            handleOpenNote(existingNote.noteId);
        } else {
            void DialogStateManager.show(LogInteractionDialog, {
                event: eventDetails,
                customer: eventDetails.customer,
                analyticsSource: 'event_drawer',
                onSuccess: refreshAndClose,
            });
        }
    };

    const handleDelete = (): void => {
        if (!eventDetails) {
            return;
        }

        void DialogStateManager.show(DeleteEventModal, {
            date,
            event: eventDetails,
            onDeleted: () => {
                remove();
                sendEvent('event_deleted', getEventMetadata(eventDetails));
            },
        });
    };

    const dateFormatted = date
        ? intlFormat(
              parseISO(date),
              { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' },
              { locale },
          )
        : '';

    const timeFormatted =
        eventDetails?.start_time && eventDetails.end_time
            ? t('card_time_format', {
                  startTime: formatISOTimeForDisplay(eventDetails.start_time),
                  endTime: formatISOTimeForDisplay(eventDetails.end_time),
              })
            : eventDetails?.start_time
              ? formatISOTimeForDisplay(eventDetails.start_time)
              : '';

    const recurrenceWeeksCount = eventDetails?.recurrence
        ? getRecurrenceWeeksCount(eventDetails.recurrence, eventDetails.date)
        : 0;

    const occurrenceDateObj = parseISO(date);

    const linkedNotes =
        eventDetails?.notes.filter((note) => isSameDay(note.occurredAt, occurrenceDateObj)) ?? [];

    // PREP-210: editing an event with a logged note can move it and detach the note, so hide
    // the edit action entirely once an interaction has been logged on this occurrence.
    const hasLoggedNote = linkedNotes.length > 0;

    if (isLoading || !eventDetails) {
        return (
            <Dialog open={visible} onClose={remove}>
                <DialogForm className="sm:max-w-[450px]">
                    <DialogForm.Content>
                        <LoadingBlockFullPage className="mt-16" />
                    </DialogForm.Content>
                </DialogForm>
            </Dialog>
        );
    }

    const googleAddressLink = buildGoogleMapsSearchUrl({ addressLine1: eventDetails.address });

    return (
        <Dialog open={visible} onClose={remove}>
            <DialogForm className="sm:max-w-[450px]">
                <DialogForm.Content className="gap-4 p-5">
                    <div className="flex items-center justify-end gap-3">
                        {isOwner && (
                            <>
                                <div className="flex items-center gap-1">
                                    {!hasLoggedNote && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            icon={PencilIcon}
                                            aria-label={t('events__edit', { ns: 'events' })}
                                            onClick={handleEdit}
                                        />
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        icon={Trash2Icon}
                                        aria-label={t('events__delete', { ns: 'events' })}
                                        onClick={handleDelete}
                                    />
                                </div>
                                <EventLogButton event={eventDetails} date={date} onClick={handleLog} />
                            </>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            icon={XIcon}
                            aria-label={t('events__close', { ns: 'events' })}
                            onClick={remove}
                        />
                    </div>
                    <InfoTag icon={TypeIcon}>
                        <Text color="neutral-900" size="lg" weight="medium">
                            {eventDetails.title ?? t('events__card_default_title')}
                        </Text>
                    </InfoTag>
                    {eventDetails.customer && (
                        <InfoTag icon={StoreIcon}>
                            <LinkText
                                href={`/customers/${eventDetails.customer.customerId}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    remove();
                                    void navigate(`/customers/${eventDetails.customerId}`);
                                }}
                            >
                                {eventDetails.customer.customerId} {eventDetails.customer.customerName}
                            </LinkText>
                        </InfoTag>
                    )}
                    {googleAddressLink && (
                        <InfoTag icon={MapPinIcon}>
                            <LinkText href={googleAddressLink} target="_blank" rel="noopener noreferrer">
                                {eventDetails.address}
                            </LinkText>
                        </InfoTag>
                    )}
                    {!!dateFormatted && (
                        <InfoTag icon={CalendarIcon}>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <Text color="neutral-900">{dateFormatted}</Text>
                                {!!recurrenceWeeksCount && (
                                    <span className="flex items-center gap-1.5">
                                        <RefreshCwIcon className="h-3.5! w-3.5!" />
                                        <Text color="neutral-900">
                                            {t('card_recurrence_weeks', { count: recurrenceWeeksCount })}
                                        </Text>
                                    </span>
                                )}
                            </div>
                        </InfoTag>
                    )}
                    {!!timeFormatted && (
                        <InfoTag icon={ClockIcon}>
                            <Text color="neutral-900">{timeFormatted}</Text>
                        </InfoTag>
                    )}
                    {eventDetails.agendaRich && (
                        <InfoTag icon={AlignLeftIcon}>
                            <RichTextViewer content={eventDetails.agendaRich} />
                        </InfoTag>
                    )}
                    {eventDetails.owner_name && (
                        <InfoTag icon={CircleUserIcon}>
                            <Text color="neutral-500">
                                {t('events__form_created_by', { ownerName: eventDetails.owner_name })}
                            </Text>
                        </InfoTag>
                    )}
                    {linkedNotes.length > 0 && (
                        <>
                            <div className="my-1 border-t border-neutral-200" />
                            <div className="flex flex-col gap-2">
                                <Text size="sm" weight="medium" color="neutral-500">
                                    {t('card_protocol_label')}
                                </Text>
                                {linkedNotes.map((note) => (
                                    <Card key={note.noteId}>
                                        <CardFront>
                                            <CardStart render={<NotebookPenIcon className="h-5 w-5" />} />
                                            <CardContent className="min-w-0">
                                                <CardHeader>
                                                    <CardTitle className="line-clamp-2 min-w-0">
                                                        {note.title ?? t('events__card_default_title')}
                                                    </CardTitle>
                                                </CardHeader>
                                                {note.attachments.length > 0 && (
                                                    <CardDetail className="flex items-center gap-1.5">
                                                        <PaperclipIcon className="h-3.5 w-3.5 shrink-0" />
                                                        {t('card_protocol_attachments', {
                                                            count: note.attachments.length,
                                                        })}
                                                    </CardDetail>
                                                )}
                                            </CardContent>
                                            <CardMainAction
                                                onClick={() => {
                                                    handleOpenNote(note.noteId);
                                                }}
                                            >
                                                <CardOpenIcon />
                                            </CardMainAction>
                                        </CardFront>
                                    </Card>
                                ))}
                            </div>
                        </>
                    )}
                </DialogForm.Content>
            </DialogForm>
        </Dialog>
    );
};

export const EventDetailsDialog =
    DialogStateManager.create<EventDetailsDialogProps>(EventDetailsDialogInternal);
